import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { boothInviteUrl, boothTheme, type BoothTheme } from '../../lib/leadBot/boothView';
import { getBoothStats } from './api';

// THE BOOTH DASHBOARD (Run P §3): a big dark screen, legible from 3 metres, refreshing every
// ~10 seconds. ⚠️ REAL COUNTS ONLY, NOTHING SEEDED: an empty room shows zeros and dashes, never
// invented figures. ⚠️ NO NAMES AND NO NUMBERS (the feed is built that way upstream), and
// distress never appears.
//
// It covers the app shell (`fixed inset-0`) rather than living inside it: the screen is a
// display, not a working page. `?accounts=a,b` widens the scope to those accounts.
// `?theme=anytime-fitness` opts in to the AF look (default render is unchanged) and shows the
// "Try the AI SMS bot" QR; `?invite=<token>` points the QR at a specific invite.

const POLL_MS = 10_000;

// Anytime Fitness 2023 brand guidelines: Violet C, Purple 2097 C, teal accent. The AF typeface
// (F37 Moon) is licensed to AF and is deliberately NOT in the repo: Archivo 800 stands in.
const AF = { violet: '#440099', purple: '#6E38D5', teal: '#3FB6A9' };

function seconds(value: number | null): string {
  if (value === null) return '—';
  return value < 60 ? `${Math.round(value)}s` : `${Math.floor(value / 60)}m ${Math.round(value % 60)}s`;
}

function time(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// AF-only animations. Both are static under prefers-reduced-motion (the celebration keeps its
// highlight, loses the scale-in; the QR ring keeps a steady glow).
const AF_CSS = `
@keyframes af-qr-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(63,182,169,.55), 0 0 18px 2px rgba(63,182,169,.35); } 50% { box-shadow: 0 0 0 14px rgba(63,182,169,0), 0 0 34px 8px rgba(63,182,169,.6); } }
@keyframes af-tour-pop { 0% { transform: scale(.9); } 40% { transform: scale(1.06); } 100% { transform: scale(1); } }
.af-qr-pulse { animation: af-qr-pulse 2s ease-in-out infinite; }
.af-tour-pop { animation: af-tour-pop .6s ease-out; box-shadow: 0 0 40px 10px rgba(63,182,169,.75); }
@media (prefers-reduced-motion: reduce) {
  .af-qr-pulse { animation: none; box-shadow: 0 0 22px 4px rgba(63,182,169,.45); }
  .af-tour-pop { animation: none; }
}`;

function Tile({
  label,
  value,
  theme,
  accent,
  celebrate,
}: {
  label: string;
  value: string;
  theme: BoothTheme;
  accent?: boolean;
  celebrate?: boolean;
}) {
  if (theme === 'anytime-fitness') {
    return (
      <div
        className={`rounded-2xl p-3 text-center ring-2 ${celebrate ? 'af-tour-pop bg-[#3FB6A9]/30' : 'bg-white/10'}`}
        style={{ ['--tw-ring-color' as string]: accent ? AF.teal : AF.purple }}
      >
        <div className="whitespace-nowrap text-5xl font-extrabold tabular-nums text-white xl:text-7xl">{value}</div>
        <div className="mt-1 text-base font-bold uppercase tracking-widest text-white/85 xl:text-xl">
          {celebrate ? 'New tour booked!' : label}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-slate-900 p-6 text-center ring-1 ring-slate-800">
      <div className="text-6xl font-bold tabular-nums text-white xl:text-8xl">{value}</div>
      <div className="mt-3 text-lg font-medium uppercase tracking-wide text-slate-400 xl:text-2xl">{label}</div>
    </div>
  );
}

/** "Try the AI SMS bot" panel. ⚠️ The invite URL is encoded in the QR and NEVER printed: the
 *  token is the authorisation, people scan. */
export function BoothQrPanel({ url }: { url: string | null }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    if (!url) {
      setSvg(null);
      return;
    }
    let cancelled = false;
    QRCode.toString(url, { type: 'svg', margin: 2, errorCorrectionLevel: 'Q', width: 400 })
      .then((out) => {
        if (!cancelled) setSvg(out);
      })
      .catch(() => {
        if (!cancelled) setSvg(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <aside
      className="flex flex-col items-center gap-2 rounded-3xl bg-white/10 p-5 text-center ring-2"
      style={{ ['--tw-ring-color' as string]: AF.purple }}
    >
      <h2 className="text-3xl font-extrabold uppercase leading-tight tracking-wide text-white xl:text-4xl">
        Scan to chat with Sam
      </h2>
      <p className="text-lg font-semibold text-white/85 xl:text-xl">your Anytime Fitness AI assistant</p>
      <p className="text-2xl font-extrabold uppercase tracking-widest text-white xl:text-3xl">
        <span style={{ color: AF.teal }}>›</span> Scan here
      </p>
      {url ? (
        <div
          className="af-qr-pulse h-[400px] w-[400px] max-w-full rounded-2xl bg-white p-2 [&>svg]:h-full [&>svg]:w-full"
          aria-label="QR code to try the AI SMS bot"
          // Generated locally by qrcode from our own URL: not user markup.
          dangerouslySetInnerHTML={{ __html: svg ?? '' }}
        />
      ) : (
        <p className="rounded-2xl bg-white/10 p-8 text-2xl font-semibold text-white">
          No live invite — create one in Bot settings
        </p>
      )}
      <p className="text-lg text-white/85 xl:text-xl">Enter your mobile — Sam will text you in seconds.</p>
      <ol className="mt-1 grid w-full grid-cols-3 gap-2 text-sm font-bold uppercase leading-tight tracking-wide text-white">
        {['Scan', 'Enter your mobile', 'Sam texts you in seconds'].map((step, i) => (
          <li key={step} className="flex flex-col items-center gap-1 rounded-xl bg-white/10 px-2 py-2">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full text-base font-extrabold text-[#2a0066]"
              style={{ background: AF.teal }}
            >
              {i + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
    </aside>
  );
}

export function BoothDashboardPage() {
  const { botId = '' } = useParams();
  const [params] = useSearchParams();
  const accounts = params.get('accounts') ?? '';
  const theme = boothTheme(params.get('theme'));
  const { data, isError, dataUpdatedAt } = useQuery({
    queryKey: ['lead-bot', 'booth', botId, accounts],
    queryFn: () => getBoothStats(botId, accounts || undefined),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
  });

  const share = data?.outsideStaffedShare ?? null;
  const af = theme === 'anytime-fitness';
  // Celebrate a NEW booking only: never on first load (prev is null until the first value lands).
  const toursNow = data?.toursBooked;
  const prevTours = useRef<number | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (toursNow === undefined) return;
    const prev = prevTours.current;
    prevTours.current = toursNow;
    if (prev === null || toursNow <= prev) return;
    setCelebrate(true);
    const t = setTimeout(() => setCelebrate(false), 3000);
    return () => clearTimeout(t);
  }, [toursNow]);
  const inviteUrl = boothInviteUrl(params.get('invite'), data?.liveInviteUrl, window.location.origin);
  const updated = isError
    ? 'Reconnecting...'
    : dataUpdatedAt
      ? `Updated ${time(new Date(dataUpdatedAt).toISOString())}`
      : 'Loading...';

  const tiles = (
    <div className={af ? 'grid grid-cols-2 gap-3 xl:grid-cols-3' : 'grid grid-cols-2 gap-6 xl:grid-cols-3'}>
      <Tile theme={theme} label={af ? 'Leads engaged' : 'Conversations started'} value={String(data?.conversationsStarted ?? 0)} />
      <Tile theme={theme} label={af ? 'Typical first reply' : 'Median time to first text'} value={seconds(data?.medianFirstTextSeconds ?? null)} />
      <Tile theme={theme} label="Replies received" value={String(data?.repliesReceived ?? 0)} />
      <Tile theme={theme} accent celebrate={af && celebrate} label="Tours booked" value={String(data?.toursBooked ?? 0)} />
      <Tile theme={theme} label="Handed to the team" value={String(data?.handedToStaff ?? 0)} />
      <Tile theme={theme} label={af ? 'Leads after hours — Sam still replied' : 'Arrived outside staffed hours'} value={share === null ? '—' : `${Math.round(share * 100)}%`} />
    </div>
  );

  const feed = (
    <section>
      <h2
        className={
          af
            ? 'mb-1 text-xl font-extrabold uppercase tracking-widest text-white'
            : 'mb-3 text-2xl font-semibold text-slate-300'
        }
      >
        What is happening
      </h2>
      {data && data.feed.length > 0 ? (
        <ul className="space-y-2">
          {(af ? data.feed.slice(0, 5) : data.feed).map((event, i) => (
            <li
              key={`${event.at}-${i}`}
              className={
                af
                  ? 'flex gap-4 rounded-xl bg-white/10 px-5 py-1.5 text-xl text-white'
                  : 'flex gap-4 rounded-xl bg-slate-900 px-5 py-3 text-2xl ring-1 ring-slate-800'
              }
            >
              <span className={af ? 'w-24 shrink-0 tabular-nums text-white/70' : 'w-24 shrink-0 tabular-nums text-slate-500'}>
                {time(event.at)}
              </span>
              <span>{event.text}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={af ? 'text-2xl text-white/70' : 'text-2xl text-slate-600'}>Waiting for the first lead...</p>
      )}
    </section>
  );

  if (!af) {
    return (
      <div className="fixed inset-0 z-50 overflow-auto bg-black p-8 text-white">
        <div className="mx-auto flex max-w-[1800px] flex-col gap-8">
          <header className="flex items-end justify-between">
            <h1 className="text-4xl font-bold xl:text-6xl">Live: leads texting the assistant</h1>
            <p className="text-lg text-slate-500">{updated}</p>
          </header>
          {tiles}
          {feed}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-auto p-6 text-white"
      style={{ background: `linear-gradient(135deg, ${AF.violet} 0%, ${AF.violet} 55%, #5a22b8 100%)` }}
      data-booth-theme="anytime-fitness"
    >
      <style>{AF_CSS}</style>
      <div className="mx-auto flex max-w-[1850px] flex-col gap-3">
        <header className="flex items-center justify-between gap-6">
          <img src="/brand/anytime-fitness/AnytimeFitness_Primary_White.png" alt="Anytime Fitness" className="h-14 w-auto" />
          <div className="flex items-center gap-5 text-sm text-white/60">
            <p>{updated}</p>
            <div className="flex items-center gap-2 uppercase tracking-widest">
              Powered by <img src="/kyck-logo-white.svg" alt="Kyck" className="h-5 w-auto" />
            </div>
          </div>
        </header>
        <div>
          <h1 className="whitespace-nowrap text-[5vw] font-extrabold uppercase leading-none tracking-tight text-white">
            Try the AI SMS Lead Engager <span style={{ color: AF.teal }}>›</span>
          </h1>
          <p
            className="mt-2 whitespace-nowrap text-[2.3vw] font-extrabold uppercase leading-tight tracking-wide"
            style={{ color: AF.teal }}
          >
            Instantly engage every Meta &amp; website lead
          </p>
        </div>
        <div className="grid gap-6 xl:grid-cols-[1fr_480px]">
          <div className="flex flex-col gap-3">
            {tiles}
            {feed}
          </div>
          <BoothQrPanel url={inviteUrl} />
        </div>
      </div>
    </div>
  );
}
